import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import qs from 'query-string';

const Integration = ({ location }) => {
  const [savedCredentials, setSavedCredentials] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    
    // Check for saved passwords when component mounts
    checkSavedPasswords();
    
    // Listen for messages from parent window
    window.addEventListener('message', handleParentMessage);
    
    // Notify parent that iframe is ready
    notifyParentReady();

    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, []);

  const checkSavedPasswords = async () => {
    try {
      // Check if we're in an iframe (cross-origin context)
      const isInIframe = window.self !== window.top;
      
      if (isInIframe) {
        // In iframe: Can't use Credential Management API due to cross-origin restrictions
        // Fall back to checking localStorage or using autofill detection
        checkLocalStorageCredentials();
        return;
      }
      
      // Try to get saved credentials from browser (only works in same-origin context)
      if (navigator.credentials && navigator.credentials.get) {
        const credential = await navigator.credentials.get({
          password: true,
          federated: {
            providers: ['https://accounts.google.com']
          }
        });
        
        if (credential && credential.type === 'password') {
          const savedData = {
            email: credential.id,
            password: credential.password,
            hasSavedPassword: true
          };
          setSavedCredentials(savedData);
          
          // Send saved credentials to parent window for autofill
          sendToParent('AUTOFILL_CREDENTIALS', savedData);
        } else {
          sendToParent('NO_SAVED_CREDENTIALS', { hasSavedPassword: false });
        }
      } else {
        // Fallback: try to detect autofilled values
        setTimeout(() => {
          checkAutofillValues();
        }, 1000);
      }
    } catch (error) {
      console.log('Error checking saved passwords:', error);
      // If Credential Management API fails, try localStorage fallback
      checkLocalStorageCredentials();
    }
  };

  const checkLocalStorageCredentials = () => {
    try {
      // For cross-origin iframe, check multiple storage locations
      const storageKeys = [
        "CWB_REMEMBER_ME",           // TeamShare format
        "CWB_LOGIN_CREDENTIALS",     // Alternative format
        "rememberedUser"             // Simple format
      ];
      
      for (const key of storageKeys) {
        const storedData = localStorage.getItem(key);
        if (storedData) {
          try {
            const parsedData = JSON.parse(storedData);
            if (parsedData.userName || parsedData.email) {
              const savedData = {
                email: parsedData?.userName || parsedData.email,
                userId: parsedData?.userId || '',
                userType: parsedData?.userType || '',
                refreshToken: parsedData?.refreshToken || '',
                hasSavedPassword: !!parsedData?.refreshToken, // True if refresh token exists
                fromLocalStorage: true,
                storageKey: key
                // Note: password is intentionally omitted for security
              };
              setSavedCredentials(savedData);
              // Send for autofill when iframe loads
              sendToParent('AUTOFILL_CREDENTIALS', savedData);
              return;
            }
          } catch (parseError) {
            console.log(`Error parsing ${key}:`, parseError);
          }
        }
      }
      
      // If no localStorage data found, try autofill detection
      // console.log('No saved credentials found in localStorage');
      setTimeout(() => {
        checkAutofillValues();
      }, 1000);
      
    } catch (error) {
      console.log('Error checking localStorage credentials:', error);
      sendToParent('NO_SAVED_CREDENTIALS', { hasSavedPassword: false });
    }
  };

  const checkAutofillValues = () => {
    // Create hidden form to check for autofilled values
    const form = document.createElement('form');
    form.style.visibility = 'hidden';
    form.style.position = 'absolute';
    
    const emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.name = 'email';
    emailInput.autocomplete = 'username';
    
    const passwordInput = document.createElement('input');
    passwordInput.type = 'password';
    passwordInput.name = 'password';
    passwordInput.autocomplete = 'current-password';
    
    form.appendChild(emailInput);
    form.appendChild(passwordInput);
    document.body.appendChild(form);
    
    // Check if values were autofilled after a short delay
    setTimeout(() => {
      if (emailInput.value || passwordInput.value) {
        const savedData = {
          email: emailInput.value,
          password: passwordInput.value,
          hasSavedPassword: true
        };
        setSavedCredentials(savedData);
        // Send for autofill when autofill detected
        sendToParent('AUTOFILL_CREDENTIALS', savedData);
      } else {
        sendToParent('NO_SAVED_CREDENTIALS', { hasSavedPassword: false });
      }
      
      // Clean up
      document.body.removeChild(form);
    }, 2000);
  };

  const handleParentMessage = (event) => {
    
    // Filter out React DevTools messages
    if (event.data && event.data.source) { 
      if (event.data.source == "react-devtools-content-script")
        return;
    }
    
    // Filter out webpack and development messages
    if (!event.data || typeof event.data !== 'object' || !event.data.type) {
      // console.log('Integration: Ignoring malformed message');
      return;
    }
    
    // Filter out webpack hot reload messages
    if (event.data.type === 'webpackOk' || event.data.type === 'webpackInvalid' || event.data.type.startsWith('webpack')) {
      // console.log('Integration: Filtering out webpack message:', event.data.type);
      return;
    }
    
    // Parse query string to determine which app is embedding the iframe
    const queryParams = qs.parse(window.location.search);
    const app = queryParams.app?.toLowerCase() || 'teamshare'; // Default to teamshare
    
    // Verify origin for security - use app-specific origins
    let allowedOrigins;
    if (window.location.origin.includes('localhost')) {
      allowedOrigins = ['http://localhost:3000'];
    } else {
      switch (app) {
        case 'agent':
          allowedOrigins = [CWB_API_ENV.agent];
          break;
        default:
          // Fallback to teamshare if unknown app
          allowedOrigins = [CWB_API_ENV.teamshare];
          break;
      }
    }
    
    if (!allowedOrigins.includes(event.origin)) {
      // console.log('Integration: Allowed origins:', allowedOrigins, 'Received origin:', event.origin);
      return;
    }
    
    const { type, data } = event.data;
    
    switch (type) {
      case 'LOGIN_REQUEST':
        handleLoginRequest(data);
        break;
      case 'SAVE_PASSWORD':
        handleSavePassword(data);
        break;
      case 'CHECK_USER_CREDENTIALS':
        handleCheckUserCredentials(data);
        break;
      case 'CHECK_ANY_SAVED_CREDENTIALS':
        handleCheckAnySavedCredentials();
        break;
      case 'LOGOUT_REQUEST':
        handleLogoutRequest();
        break;
      case 'USER_LOGGED_IN':
        handleUserLoggedIn(data);
        break;
      default:
        console.log('Integration: Unknown message type:', type);
        break;
    }
  };

  const handleLoginRequest = async (loginData) => {
    const { email, password, savePassword } = loginData;
    
    try {
      // Note: We no longer save password during login request
      // Password saving is handled after successful login via USER_LOGGED_IN message
      
      // Send login response back to parent
      sendToParent('LOGIN_RESPONSE', { 
        success: true, 
        savedPassword: savePassword 
      });
      
    } catch (error) {
      console.error('Integration: Login error:', error);
      sendToParent('LOGIN_RESPONSE', { 
        success: false, 
        error: error.message 
      });
    }
  };

  const handleCheckAnySavedCredentials = async () => {
    try {
      // Check localStorage for any saved credentials (without specific email)
      const rememberMeData = localStorage.getItem("CWB_REMEMBER_ME");
      
      if (rememberMeData) {
        try {
          const parsedData = JSON.parse(rememberMeData);
          
          if (parsedData.userName || parsedData.email) {
            sendToParent('AUTOFILL_CREDENTIALS', {
              email: parsedData?.userName || parsedData?.email,
              userId: parsedData?.userId || '',
              userType: parsedData?.userType || '',
              refreshToken: parsedData?.refreshToken || '',
              hasSavedPassword: !!parsedData?.refreshToken, // True if refresh token exists
              fromLocalStorage: true,
              anyUser: true
              // Note: password is intentionally omitted for security
            });
            return;
          }
        } catch (parseError) {
          console.log('Integration: Error parsing localStorage data:', parseError);
        }
      }
      
      // Try Credential Management API for any saved credentials
      if (navigator.credentials && navigator.credentials.get) {
        try {
          const credential = await navigator.credentials.get({ 
            password: true,
            mediation: 'silent' // Don't show UI, just check for existing credentials
          });
          if (credential && credential.type === 'password') {
            sendToParent('AUTOFILL_CREDENTIALS', {
              email: credential.id,
              password: credential.password,
              hasSavedPassword: true,
              fromCredentialAPI: true,
              anyUser: true
            });
            return;
          }
        } catch (credError) {
          console.log('Integration: Credential API check failed:', credError);
        }
      }
      
      // No saved credentials found
      sendToParent('NO_SAVED_CREDENTIALS', { 
        hasSavedPassword: false,
        anyUser: true 
      });
      
    } catch (error) {
      console.error('Integration: Error checking any saved credentials:', error);
      sendToParent('NO_SAVED_CREDENTIALS', { 
        hasSavedPassword: false, 
        error: error.message,
        anyUser: true 
      });
    }
  };

  const handleCheckUserCredentials = async (data) => {
    const { email } = data;
    
    try {
      // Check localStorage for saved credentials for this specific user
      const rememberMeData = localStorage.getItem("CWB_REMEMBER_ME");
      
      if (rememberMeData) {
        try {
          const parsedData = JSON.parse(rememberMeData);
          
          if (parsedData.userName === email) {
            sendToParent('AUTOFILL_CREDENTIALS', {
              email: parsedData?.userName,
              userId: parsedData?.userId || '',
              userType: parsedData?.userType || '',
              refreshToken: parsedData?.refreshToken || '',
              hasSavedPassword: !!parsedData?.refreshToken, // True if refresh token exists
              fromLocalStorage: true,
              userSpecific: true
              // Note: password is intentionally omitted for security
            });
            return;
          } else {
            // console.log('Integration: Email mismatch - stored:', parsedData.userName, 'requested:', email);
          }
        } catch (parseError) {
          console.log('Integration: Error parsing localStorage data:', parseError);
        }
      } else {
        console.log('Integration: No CWB_REMEMBER_ME data found in localStorage');
      }
      
      // Check if browser has saved credentials for this user (if not in iframe)
      const isInIframe = window.self !== window.top;
      
      // Try Credential Management API (works in iframe too for same-origin)
      if (navigator.credentials && navigator.credentials.get) {
        try {
          const credential = await navigator.credentials.get({ 
            password: true,
            mediation: 'silent' // Don't show UI, just check for existing credentials
          });
          if (credential && credential.type === 'password' && credential.id === email) {
            sendToParent('AUTOFILL_CREDENTIALS', {
              email: credential.id,
              password: credential.password, // Include the actual password
              hasSavedPassword: true,
              fromCredentialAPI: true,
              userSpecific: true
            });
            return;
          } else {
            // console.log('Integration: No credential API match for user:', email);
          }
        } catch (credError) {
          console.log('Integration: Credential API check failed:', credError);
        }
      }
      
      // No saved credentials found for this user
      sendToParent('NO_SAVED_CREDENTIALS', { 
        hasSavedPassword: false,
        email: email,
        userSpecific: true 
      });
      
    } catch (error) {
      console.error('Integration: Error checking user credentials:', error);
      sendToParent('NO_SAVED_CREDENTIALS', { 
        hasSavedPassword: false, 
        error: error.message,
        userSpecific: true 
      });
    }
  };

  const handleUserLoggedIn = (data) => {
    const { email, savePassword, refreshToken, userId, userType } = data;
    
    if (savePassword && refreshToken) {
      // User wants to save credentials, store only refresh token and metadata
      const rememberMeData = {
        userName: email,
        userId: userId || "",
        userType: userType || "",
        refreshToken: refreshToken,
        timestamp: Date.now()
        // Note: password is intentionally omitted for security
      };
      localStorage.setItem("CWB_REMEMBER_ME", JSON.stringify(rememberMeData));
      sendToParent('CREDENTIALS_SAVED', { success: true, method: 'localStorage' });
    } else if (!savePassword) {
      // User unchecked save password, remove any existing saved data for this user
      const existingData = localStorage.getItem("CWB_REMEMBER_ME");
      if (existingData) {
        try {
          const parsed = JSON.parse(existingData);
          if (parsed.userName === email) {
            localStorage.removeItem("CWB_REMEMBER_ME");
            sendToParent('CREDENTIALS_REMOVED', { success: true });
          } else {
            // console.log('Integration: Existing data is for different user, keeping it');
          }
        } catch (e) {
          console.log('Integration: Error parsing existing data:', e);
        }
      } else {
        // console.log('Integration: No existing data to remove');
      }
    } else {
      console.log('Integration: No refresh token provided, cannot save credentials');
    }
  };

  const handleLogoutRequest = () => {
    try {
      // Clear iframe's localStorage
      localStorage.removeItem("CWB_REMEMBER_ME");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("CWB_LOGIN_CREDENTIALS");
      localStorage.removeItem("rememberedUser");
      // Reset component state
      setSavedCredentials(null);
      setIsReady(false);
      // Notify parent that logout is complete
      sendToParent('LOGOUT_COMPLETE', { success: true });
            
    } catch (error) {
      console.error('Iframe logout error:', error);
      sendToParent('LOGOUT_COMPLETE', { success: false, error: error.message });
    }
  };

  // Removed handleSavePassword function - no longer needed since we only save refresh tokens

  const sendToParent = (type, data) => {
    // console.log('Integration: window.parent !== window:', window.parent !== window);
    
    if (window.parent !== window) {
      try {
        // Parse query string to determine which app is embedding the iframe
        const queryParams = qs.parse(window.location.search);
        const app = queryParams.app?.toLowerCase() || 'teamshare'; // Default to teamshare
        
        // Use app-specific origin for postMessage
        let targetOrigins;
        if (window.location.origin.includes('localhost')) {
          targetOrigins = ['http://localhost:3000'];
        } else {
          switch (app) {
            case 'teamshare':
              targetOrigins = [CWB_API_ENV.teamshare];
              break;
            case 'agent':
              targetOrigins = [CWB_API_ENV.agent];
              break;
            default:
              // Fallback to teamshare if unknown app
              targetOrigins = [CWB_API_ENV.teamshare];
              break;
          }
        }
        
        let messageSucceeded = false;
        for (const targetOrigin of targetOrigins) {
          try {
            window.parent.postMessage({ type, data }, targetOrigin);
            messageSucceeded = true;
            break;
          } catch (originError) {
            // console.log(`Integration: Failed to send to ${targetOrigin}:`, originError.message);
            continue;
          }
        }
        
        if (!messageSucceeded) {
          throw new Error('All target origins failed');
        }
      } catch (error) {
        console.log('Integration: All specific origins failed, trying wildcard as final fallback:', error.message);
        
        try {
          // Final fallback to wildcard
          window.parent.postMessage({ type, data }, '*');
        } catch (fallbackError) {
          console.error('Integration: All postMessage attempts failed:', fallbackError);
        }
      }
    } else {
      // console.log('Integration: No parent window found, not sending message');
    }
  };

  const notifyParentReady = () => {
    setIsReady(true);
    sendToParent('IFRAME_READY', { ready: true });
  };

  return (
    <Container>
      <Hidden>
        {/* Hidden form for credential detection */}
        <input
          type="email"
          name="email"
          autoComplete="username"
          style={{ opacity: 0, position: 'absolute', left: '-9999px' }}
        />
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          style={{ opacity: 0, position: 'absolute', left: '-9999px' }}
        />
      </Hidden>
      
      {/* Debug info - remove in production */}
      {(process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'staging') && (
        <DebugInfo>
          <p>Integration iframe ready: {isReady ? 'Yes' : 'No'}</p>
          <p>App: {qs.parse(window.location.search).app || 'teamshare (default)'}</p>
          <p>Saved credentials: {savedCredentials ? 'Found' : 'None'}</p>
          {savedCredentials && (
            <p>Email: {savedCredentials.email}</p>
          )}
        </DebugInfo>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100vh;
  background: transparent;
  padding: 20px;
`;

const Hidden = styled.div`
  visibility: hidden;
  position: absolute;
  top: -9999px;
  left: -9999px;
`;

const DebugInfo = styled.div`
  font-size: 12px;
  color: #666;
  background: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  
  p {
    margin: 5px 0;
  }
`;

export default Integration;